import React, {useMemo, useState} from 'react';
import {Card, Col, Input, message, Row, Space, Steps} from 'antd';
import dayjs from 'dayjs';
import PrimaryButton from '../../../../components/common/PrimaryButton';
import IssueSummaryCard from '../components/IssueSummaryCard';
import {useTranslation} from '../../../../hooks/useTranslation';
import {useDateFormat} from '../../../../hooks/useDateFormat';
import {useApproveIssue} from '../../../../hooks/issues/useIssues';
import {tokens} from '../../../../theme/tokens';
import {extractErrorMessage} from '../../../../utils/apiError';

interface Props {
  issueId?: string;
  activePanel: string;
}

// ---------------------------------------------------------------------------
// Step model — matches docs/giro-issue-approval-page.md § 1.4.2.
// TODO: replace with `useApprovalHistory(issueId)` once the backend exposes
//       the approval chain. The list below mirrors the Figma seeded entries.
// ---------------------------------------------------------------------------

type StepStatus = 'finish' | 'process' | 'wait';

interface ApprovalStep {
  key: string;
  titleKey: string;
  defaultTitle: string;
  status: StepStatus;
  by?: string;
  date?: string; // ISO timestamp; rendered via useDateFormat()
  note?: string;
  awaiting?: boolean; // true when this is the current user's pending step
}

const MOCK_STEPS: ApprovalStep[] = [
  {
    key: 'submitted',
    titleKey: 'issues.approval.steps.submitted',
    defaultTitle: 'Submitted',
    status: 'finish',
    by: 'Bob Wilson',
    date: '2026-05-09T08:47:00Z',
    note: 'Issue submitted for review',
  },
  {
    key: 'team-lead',
    titleKey: 'issues.approval.steps.teamLead',
    defaultTitle: 'Team Lead Approval',
    status: 'finish',
    by: 'Jane Smith',
    date: '2026-05-10T08:47:00Z',
    note: 'Approved. Critical issue requiring immediate attention.',
  },
  {
    key: 'manager',
    titleKey: 'issues.approval.steps.manager',
    defaultTitle: 'Pending Manager Approval',
    status: 'process',
    by: 'John Doe',
    note: 'Awaiting response',
    awaiting: true,
  },
  {
    key: 'final',
    titleKey: 'issues.approval.steps.final',
    defaultTitle: 'Final Review',
    status: 'wait',
    note: 'Pending previous approvals',
  },
];

// ---------------------------------------------------------------------------
// Issue Summary — `Badge status` ↔ issue status mapping.
// TODO: derive from `useIssueDetail(issueId)` once the status map is exposed.
// ---------------------------------------------------------------------------

interface IssueSummary {
  code: string;
  statusLabel: string;
  statusBadge: 'success' | 'processing' | 'warning' | 'error' | 'default';
  reportedBy: string;
  reportedOn: string;
}

const MOCK_SUMMARY: IssueSummary = {
  code: 'ISS-20260511-785',
  statusLabel: 'In Progress',
  statusBadge: 'success',
  reportedBy: 'Bob Wilson',
  reportedOn: '2026-05-11T08:47:00Z',
};

// ===========================================================================
// Component
// ===========================================================================

const IssueApproval: React.FC<Props> = ({issueId}) => {
  const {t} = useTranslation();
  const {dateTimeFormat} = useDateFormat();
  const [messageApi, contextHolder] = message.useMessage();

  const approveMutation = useApproveIssue();

  const [comment, setComment] = useState('');
  const [actionLoading, setActionLoading] = useState<'approve' | 'reject' | null>(null);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  const steps = MOCK_STEPS;
  const summary = MOCK_SUMMARY;

  const currentIndex = useMemo(
    () => steps.findIndex((s) => s.status === 'process'),
    [steps]
  );
  const awaitingMyDecision = currentIndex >= 0 && steps[currentIndex].awaiting === true;

  const formatStepDescription = (step: ApprovalStep): React.ReactNode => {
    const prefix = step.status === 'finish' ? t('issues.approval.by') : t('issues.approval.assignedTo');
    const hasBy = !!step.by;
    const hasDate = !!step.date;
    const hasNote = !!step.note;

    if (!hasBy && !hasDate && !hasNote) return null;

    return (
      <span className="gt-approval-step-desc">
        {hasBy && (
          <>
            {prefix}: <span>{step.by}</span>
          </>
        )}
        {hasDate && (
          <>
            {hasBy ? ' · ' : ''}
            {dayjs(step.date).format(dateTimeFormat)}
          </>
        )}
        {hasNote && (
          <>
            {(hasBy || hasDate) ? ' — ' : ''}
            {step.note}
          </>
        )}
      </span>
    );
  };

  const handleApprove = async () => {
    setActionLoading('approve');
    try {
      await approveMutation.mutateAsync({uid: issueId, comments: comment.trim() || undefined});
      messageApi.success(t('issues.messages.approvalSuccess'));
      setComment('');
    } catch (error) {
      messageApi.error(extractErrorMessage(error, t('issues.approval.messages.approveError')));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) {
      setCommentError(t('issues.approval.form.comments.validation.requiredForReject'));
      return;
    }
    setCommentError(null);
    setActionLoading('reject');
    try {
      // TODO: replace with a dedicated `useRejectIssue()` mutation once available.
      await approveMutation.mutateAsync({uid: issueId, comments: comment.trim(), action: 'reject'});
      messageApi.success(t('issues.approval.messages.rejectSuccess'));
      setComment('');
    } catch (error) {
      messageApi.error(extractErrorMessage(error, t('issues.approval.messages.rejectError')));
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) {
      setCommentError(t('issues.approval.form.comments.validation.required'));
      return;
    }
    setCommentError(null);
    setCommentSaving(true);
    try {
      // TODO: post to the comments endpoint and invalidate ['issue', id, 'comments'].
      await new Promise((resolve) => setTimeout(resolve, 300));
      messageApi.success(t('issues.approval.messages.commentSuccess'));
      setComment('');
    } finally {
      setCommentSaving(false);
    }
  };

  return (
    <div className="gt-issue-approval">
      {contextHolder}
      <Row gutter={[tokens.spacing.x24, tokens.spacing.x24]}>
        <Col xs={24} lg={16}>
          <Card className="gt-approval-card">
            <Steps
              orientation="vertical"
              type={"dot"}
              size="medium"
              className="gt-approval-steps"
              current={currentIndex >= 0 ? currentIndex : steps.length}
              items={steps.map((step) => ({
                key: step.key,
                title: t(step.titleKey) === step.titleKey ? step.defaultTitle : t(step.titleKey),
                status: step.status,
                description: formatStepDescription(step),
              }))}
            />

            <div className="gt-approval-comment">
              <Input.TextArea
                rows={4}
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  if (commentError) setCommentError(null);
                }}
                placeholder={t('issues.approval.form.comments.placeHolder')}
                status={commentError ? 'error' : undefined}
                className="gt-approval-textarea"
              />
              {commentError && (
                <div className="gt-approval-error">{commentError}</div>
              )}
            </div>

            <Space size={tokens.spacing.x8} className="gt-approval-actions">
              {awaitingMyDecision && (
                <>
                  <PrimaryButton
                    type="primary"
                    loading={actionLoading === 'approve'}
                    disabled={actionLoading === 'reject' || commentSaving}
                    onClick={handleApprove}
                  >
                    {t('issues.approval.actions.approve')}
                  </PrimaryButton>
                  <PrimaryButton
                    loading={actionLoading === 'reject'}
                    disabled={actionLoading === 'approve' || commentSaving}
                    onClick={handleReject}
                  >
                    {t('issues.approval.actions.reject')}
                  </PrimaryButton>
                </>
              )}
              <PrimaryButton
                loading={commentSaving}
                disabled={actionLoading !== null}
                onClick={handleAddComment}
              >
                {t('issues.approval.actions.addComment')}
              </PrimaryButton>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <IssueSummaryCard
            code={summary.code}
            statusLabel={summary.statusLabel}
            statusColor={summary.statusBadge}
            reportedBy={summary.reportedBy}
            reportedOn={summary.reportedOn}
          />
        </Col>
      </Row>
    </div>
  );
};

export default IssueApproval;
